import React from 'react';
import { ChevronDown } from 'lucide-react';
import type { SessionLite } from '../../../../../types/session-lite';
import { SessionRow } from './SessionRow';
import { EmptyState } from '../../../../../components/common/EmptyState';
import { SESSION_APPOINTMENT_TYPES } from '../../../../../hooks/api/config';
import { parseSessionDate, toISODate } from '../../../../../utils/sessions';
import { BA_TIMEZONE } from '../../../../../utils/timezone';

type Props = {
  sessions: SessionLite[];
  onViewSession: (session: SessionLite) => void;
  onAddNote?: (id: string) => void;
  onSchedule?: () => void;
  canSchedule?: boolean;
  dayISO?: string;
  therapistTz?: string;
};

const getHourLabel = (dateStr: string, timeZone: string): string => {
  const d = parseSessionDate(dateStr);
  if (isNaN(d.getTime())) return '';
  const label = d.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  });
  return String(label).replace(/\s/g, ' ').toLowerCase();
};

/**
 * Separador de hora actual.
 * Se muestra únicamente cuando el día seleccionado es hoy, entre la última sesión
 * pasada y la primera futura (o al principio/final si todas son futuras/pasadas).
 */
const NowIndicator: React.FC = () => (
  <div
    data-testid="sessions-now-indicator"
    aria-hidden="true"
    className="flex items-center my-1.5"
  >
    <div
      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
      style={{ background: 'var(--lila-dark, #665090)' }}
    />
    <div
      className="flex-1 h-0.5"
      style={{ background: 'var(--lila-dark, #665090)' }}
    />
  </div>
);

export const SessionsTimeline: React.FC<Props> = ({
  sessions,
  onViewSession,
  onAddNote,
  onSchedule: _onSchedule,
  canSchedule: _canSchedule = true,
  dayISO,
  therapistTz = BA_TIMEZONE,
}) => {
  const todayISO = toISODate(new Date());
  const isToday = !!dayISO && dayISO === todayISO;
  const isPastDay = !!dayISO && dayISO < todayISO;

  const [isCancelledOpen, setIsCancelledOpen] = React.useState(false);

  // nowMs se sincroniza con el reloj real cada 60 s, pero solo cuando el día
  // seleccionado es hoy (para no consumir recursos en días pasados/futuros).
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!isToday) return;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [isToday]);

  // La única sesión que debe mostrar "Ingresar a la sesión" es la próxima
  // (la más cercana en el tiempo entre las no canceladas dentro de la ventana de 6h)
  const joinableIds = React.useMemo(() => {
    const now = Date.now();
    const candidates: { id: string; startMs: number; inProgress: boolean }[] = [];
    for (const s of sessions) {
      if (s.isCancelled) continue;
      const startMs = parseSessionDate(s.date).getTime();
      const matchedType = SESSION_APPOINTMENT_TYPES.find(t => t.name === s.therapyType);
      const duration = matchedType?.duration || 60;
      const endMs = startMs + duration * 60000;
      if (startMs <= now + 6 * 3600000 && now < endMs) {
        candidates.push({ id: s.id, startMs, inProgress: now >= startMs });
      }
    }
    // Max 2: the in-progress session (if any) + the next upcoming one
    const ids = new Set<string>();
    const inProgress = candidates.find(c => c.inProgress);
    if (inProgress) ids.add(inProgress.id);
    const upcoming = candidates
      .filter(c => !c.inProgress)
      .sort((a, b) => a.startMs - b.startMs)[0];
    if (upcoming) ids.add(upcoming.id);
    return ids;
  }, [sessions]);

  // Sesiones visibles (no canceladas) ordenadas cronológicamente.
  const flatSorted = React.useMemo(
    () =>
      sessions
        .filter(s => !s.isCancelled)
        .slice()
        .sort((a, b) => parseSessionDate(a.date).getTime() - parseSessionDate(b.date).getTime()),
    [sessions],
  );

  // Índice de inserción del indicador: primera sesión cuyo inicio supera nowMs.
  // Si todas pasaron → flatSorted.length (indicador al final).
  // Si ninguna pasó   → 0 (indicador al principio).
  const nowInsertionIdx = React.useMemo(() => {
    if (!isToday) return -1;
    for (let i = 0; i < flatSorted.length; i++) {
      if (parseSessionDate(flatSorted[i].date).getTime() > nowMs) return i;
    }
    return flatSorted.length;
  }, [isToday, flatSorted, nowMs]);

  // Si no hay sesiones en el día, mostrar un único empty state
  if (!sessions || sessions.length === 0) {
    return (
      <EmptyState
        title="Este día no tienes sesiones agendadas"
        description="Agenda tus sesiones con anticipación para seguir acompañando a nuestros pacientes."
        actionLabel="Agendar sesión"
        onAction={_onSchedule}
        showAction={_canSchedule && !isPastDay}
        actionButtonId="sessions-empty-schedule-btn"
      />
    );
  }

  return (
    <div className="sess-timeline-wrap">
      <style>{`
        .sess-timeline-wrap { display:block; padding-bottom: 1.5rem; }
        .sess-slot { border-top:1px solid #EEF0F4; padding-top:12px; min-height:78px; }
        .sess-stack { display:flex; flex-direction:column; gap:10px; width:100%; }
      `}</style>
      <div className="flex flex-col">
        {(() => {
          const elements: React.ReactNode[] = [];
          let lastHourLabel = '';

          flatSorted.forEach((s, idx) => {
            const hourLabel = getHourLabel(s.date, therapistTz);
            const isNewHour = hourLabel !== lastHourLabel;
            lastHourLabel = hourLabel;

            // Insertar indicador ANTES de la primera sesión futura.
            if (isToday && idx === nowInsertionIdx) {
              elements.push(<NowIndicator key="now-indicator" />);
            }

            const startMs = parseSessionDate(s.date).getTime();
            const isPast = !isNaN(startMs) && startMs < nowMs;

            elements.push(
              <div
                key={s.id}
                className={isNewHour && idx > 0 ? 'sess-slot' : ''}
                style={!isNewHour && idx > 0 ? { marginTop: 10 } : undefined}
              >
                <SessionRow
                  session={s}
                  onViewSession={onViewSession}
                  onAddNote={onAddNote}
                  isPast={isPast}
                  isNextJoinable={joinableIds.has(s.id)}
                />
              </div>
            );
          });

          // Si todas las sesiones ya pasaron, mostrar el indicador al final.
          if (isToday && nowInsertionIdx >= flatSorted.length && flatSorted.length > 0) {
            elements.push(<NowIndicator key="now-indicator" />);
          }

          return elements;
        })()}

        {/* Sección de canceladas: acordeón único colapsado por defecto */}
        {sessions.some(s => s.isCancelled) && (
          <div className="sess-slot">
            <div className="sess-stack">
              {(() => {
                const cancelled = sessions.filter(s => s.isCancelled);
                return (
                  <div>
                    <button
                      type="button"
                      id="sess-tour-cancelled"
                      data-testid="cancelled-section-toggle"
                      aria-expanded={isCancelledOpen}
                      onClick={() => setIsCancelledOpen(prev => !prev)}
                      className="flex items-center justify-between w-full px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-sm font-medium text-gray-700 hover:bg-red-100 transition-colors"
                    >
                      <span>Sesiones Canceladas ({cancelled.length})</span>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isCancelledOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isCancelledOpen && (
                      <div className="mt-2 space-y-2">
                        {cancelled.map(s => (
                          <SessionRow
                            key={s.id}
                            session={s}
                            onViewSession={onViewSession}
                            onAddNote={onAddNote}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionsTimeline;
